import React, { useMemo } from 'react';
import { useTask } from '../contexts/TaskContext';
import { CheckCircle, Clock, AlertCircle, List } from 'lucide-react';

const TaskDashboard = () => {
    const { tasks = [], lists = [] } = useTask();

    // --- Statistics Calculation ---
    const stats = useMemo(() => {
        const total = tasks.length;
        const finalizadas = tasks.filter(t => t.estado === 'finalizado').length;
        const sinFinalizar = total - finalizadas;

        const now = new Date();
        const conRetraso = tasks.filter(t => {
            if (t.estado === 'finalizado' || !t.fechaVencimiento) return false;
            const dueDate = t.fechaVencimiento.seconds ? new Date(t.fechaVencimiento.seconds * 1000) : new Date(t.fechaVencimiento);
            return dueDate < now;
        }).length;

        return { total, finalizadas, sinFinalizar, conRetraso };
    }, [tasks]);

    // --- Charts Data Preparation ---

    // 1. Tasks by List (Section)
    const tasksByList = useMemo(() => {
        const counts = {};
        tasks.forEach(t => {
            if (t.estado !== 'finalizado') {
                const listName = lists.find(l => l.id === t.listId)?.nombre || 'Desconocido';
                counts[listName] = (counts[listName] || 0) + 1;
            }
        });
        return Object.entries(counts).map(([name, count]) => ({ name, count }));
    }, [tasks, lists]);

    // 2. Tasks by Assignee
    const tasksByAssignee = useMemo(() => {
        const counts = {};
        tasks.forEach(t => {
            if (t.estado !== 'finalizado') {
                const assignees = Array.isArray(t.asignados) && t.asignados.length > 0
                    ? t.asignados
                    : (typeof t.asignados === 'string' && t.asignados.trim() ? [t.asignados] : ['Sin asignar']);

                assignees.forEach(a => {
                    const strA = typeof a === 'string' ? a : String(a);
                    const name = strA.includes('@') ? strA.split('@')[0] : strA; // Simple name extraction
                    counts[name] = (counts[name] || 0) + 1;
                });
            }
        });
        return Object.entries(counts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 7);
    }, [tasks]);

    // --- Simple Chart Components (SVG) ---

    const BarChart = ({ data, color = "#8b5cf6" }) => {
        if (!data || data.length === 0) return <div className="text-gray-400 text-sm text-center py-10">No hay datos disponibles</div>;

        const max = Math.max(...data.map(d => d.count));
        const height = 150;
        const barWidth = 30;
        const gap = 20;
        const width = data.length * (barWidth + gap);

        return (
            <div className="overflow-x-auto">
                <svg width={Math.max(width, 300)} height={height + 40} className="mt-4">
                    {data.map((d, i) => {
                        const barHeight = (d.count / max) * height;
                        const x = i * (barWidth + gap) + 10;
                        const y = height - barHeight;
                        return (
                            <g key={i}>
                                <rect x={x} y={y} width={barWidth} height={barHeight} fill={color} rx="4" />
                                <text x={x + barWidth / 2} y={y - 5} textAnchor="middle" fontSize="10" fill="#6b7280">{d.count}</text>
                                <text x={x + barWidth / 2} y={height + 15} textAnchor="middle" fontSize="10" fill="#6b7280" style={{ maxWidth: barWidth }} className="truncate">{d.name.substring(0, 8)}</text>
                            </g>
                        );
                    })}
                </svg>
            </div>
        );
    };

    const DonutChart = ({ completed, remaining }) => {
        const total = completed + remaining;
        if (total === 0) return <div className="text-gray-400 text-sm text-center py-10">No hay datos</div>;

        const radius = 60;
        const circumference = 2 * Math.PI * radius;
        const completedPercent = completed / total;
        const completedOffset = circumference - (completedPercent * circumference);

        return (
            <div className="flex items-center justify-center relative h-48">
                <svg width="140" height="140" viewBox="0 0 140 140" className="transform -rotate-90">
                    <circle cx="70" cy="70" r={radius} fill="transparent" stroke="#ede9fe" strokeWidth="20" />
                    <circle
                        cx="70" cy="70" r={radius}
                        fill="transparent"
                        stroke="#8b5cf6"
                        strokeWidth="20"
                        strokeDasharray={circumference}
                        strokeDashoffset={completedOffset}
                        strokeLinecap="round"
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-gray-800">{total}</span>
                    <span className="text-xs text-gray-500">Total</span>
                </div>
            </div>
        );
    };

    return (
        <div className="flex-1 bg-gray-50 p-6 overflow-y-auto">
            <div className="max-w-7xl mx-auto space-y-6">

                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-start mb-2">
                            <h3 className="text-gray-500 text-sm font-medium">Total de tareas finalizadas</h3>
                            <CheckCircle className="text-green-500 w-5 h-5" />
                        </div>
                        <p className="text-3xl font-bold text-gray-800">{stats.finalizadas}</p>
                        <p className="text-xs text-gray-400 mt-1">1 filtro</p>
                    </div>

                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-start mb-2">
                            <h3 className="text-gray-500 text-sm font-medium">Total de tareas sin finalizar</h3>
                            <Clock className="text-blue-500 w-5 h-5" />
                        </div>
                        <p className="text-3xl font-bold text-gray-800">{stats.sinFinalizar}</p>
                        <p className="text-xs text-gray-400 mt-1">1 filtro</p>
                    </div>

                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-start mb-2">
                            <h3 className="text-gray-500 text-sm font-medium">Total de tareas con retraso</h3>
                            <AlertCircle className="text-red-500 w-5 h-5" />
                        </div>
                        <p className="text-3xl font-bold text-gray-800">{stats.conRetraso}</p>
                        <p className="text-xs text-gray-400 mt-1">1 filtro</p>
                    </div>

                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-start mb-2">
                            <h3 className="text-gray-500 text-sm font-medium">Tareas en total</h3>
                            <List className="text-purple-500 w-5 h-5" />
                        </div>
                        <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
                        <p className="text-xs text-gray-400 mt-1">Sin filtros</p>
                    </div>
                </div>

                {/* Charts Row 1 */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Tasks by Section (List) */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <h3 className="text-gray-700 font-semibold mb-4">Total de tareas sin finalizar por sección</h3>
                        <BarChart data={tasksByList} color="#a78bfa" />
                    </div>

                    {/* Tasks by Status (Donut) */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <h3 className="text-gray-700 font-semibold mb-4">Total de tareas por estado de finalización</h3>
                        <div className="flex items-center justify-around">
                            <DonutChart completed={stats.finalizadas} remaining={stats.sinFinalizar} />
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                                    <span className="text-sm text-gray-600">Finalizadas ({stats.finalizadas})</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-purple-100"></div>
                                    <span className="text-sm text-gray-600">Sin finalizar ({stats.sinFinalizar})</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Charts Row 2 */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Tasks by Assignee */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <h3 className="text-gray-700 font-semibold mb-4">Total de próximas tareas por responsable</h3>
                        <BarChart data={tasksByAssignee} color="#e0e7ff" />
                    </div>

                    {/* Tasks Over Time */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <h3 className="text-gray-700 font-semibold mb-4">Finalización de tareas (Últimos 6 meses)</h3>
                        <div className="h-40 flex items-end justify-between gap-1 px-4">
                            {(() => {
                                // Group completed tasks by month
                                const last6Months = [];
                                const today = new Date();
                                for (let i = 5; i >= 0; i--) {
                                    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
                                    last6Months.push({
                                        monthRef: d,
                                        label: d.toLocaleDateString('es-ES', { month: 'short' }),
                                        count: 0
                                    });
                                }

                                tasks.forEach(t => {
                                    if (t.estado === 'finalizado') {
                                        // Use updatedAt as a proxy for completion date if completedAt is missing
                                        const dateVal = t.completedAt || t.updatedAt;
                                        if (!dateVal) return;

                                        const date = dateVal.seconds ? new Date(dateVal.seconds * 1000) : new Date(dateVal);

                                        const monthIdx = last6Months.findIndex(m =>
                                            m.monthRef.getMonth() === date.getMonth() &&
                                            m.monthRef.getFullYear() === date.getFullYear()
                                        );

                                        if (monthIdx !== -1) {
                                            last6Months[monthIdx].count++;
                                        }
                                    }
                                });

                                const maxCount = Math.max(...last6Months.map(m => m.count), 1); // Avoid div by zero

                                return last6Months.map((m, i) => (
                                    <div key={i} className="w-full bg-purple-100 hover:bg-purple-200 rounded-t transition-colors relative group" style={{ height: `${(m.count / maxCount) * 100}%` }}>
                                        {m.count > 0 && (
                                            <div className="absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-1 rounded opacity-0 group-hover:opacity-100">{m.count}</div>
                                        )}
                                        {/* Min height for empty bars */}
                                        {m.count === 0 && <div className="h-0.5 w-full bg-gray-200 absolute bottom-0"></div>}
                                    </div>
                                ));
                            })()}
                        </div>
                        <div className="flex justify-between mt-2 text-xs text-gray-400">
                            {(() => {
                                const months = [];
                                const today = new Date();
                                for (let i = 5; i >= 0; i--) {
                                    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
                                    months.push(d.toLocaleDateString('es-ES', { month: 'short' }));
                                }
                                return months.map((m, i) => <span key={i}>{m}</span>);
                            })()}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default TaskDashboard;
